import React, { useMemo } from 'react';
import { Breadcrumb } from 'antd';
import styled from 'styled-components';
import { useEpiContext } from '../context/EpiContext';
import { getSectionAncestry } from '../utils/schemaUtils';
import { sectionLabel } from '../utils/epiHelpers';

/**
 * Where the selected section sits in the document, as a clickable trail. The
 * tree scrolls, so the path to a deep section is often off-screen while it is
 * being edited.
 */
const SectionBreadcrumb: React.FC = () => {
  const { sectionTree, selectedSection, setSelectedSection } = useEpiContext();

  const trail = useMemo(
    () =>
      selectedSection
        ? getSectionAncestry(sectionTree, selectedSection.path)
        : [],
    [sectionTree, selectedSection]
  );

  // A one-item trail is just the heading shown below it.
  if (trail.length < 2) return null;

  const items = trail.map((node, i) => {
    // Repeatable instances share a title; the nature/type tells them apart.
    const title = sectionLabel(node.title);
    const label = node.natureType ? `${title} — ${node.natureType}` : title;
    const isCurrent = i === trail.length - 1;
    return {
      title: isCurrent ? (
        <Current title={label}>{label}</Current>
      ) : (
        <Crumb title={label} onClick={() => setSelectedSection(node)}>
          {label}
        </Crumb>
      ),
    };
  });

  return <StyledBreadcrumb separator="›" items={items} />;
};

export default SectionBreadcrumb;

const StyledBreadcrumb = styled(Breadcrumb)`
  margin-bottom: 12px;
  font-size: 13px;

  .ant-breadcrumb-separator {
    color: ${({ theme }) => theme?.colors?.textSecondary || '#475067'};
    margin-inline: 6px;
  }
`;

const Crumb = styled.span`
  cursor: pointer;
  color: ${({ theme }) => theme?.colors?.textSecondary || '#475067'};

  &:hover {
    color: ${({ theme }) => theme?.colors?.primary || '#2563eb'};
  }
`;

const Current = styled.span`
  color: ${({ theme }) => theme?.colors?.textPrimary || '#0f1729'};
`;
